type StorageKind = "local" | "session";

interface SafeStorageApi {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

const memoryFallback: Record<StorageKind, Map<string, string>> = {
  local: new Map(),
  session: new Map(),
};

const availabilityCache = new Map<StorageKind, boolean>();

function getNativeStorage(kind: StorageKind): Storage | null {
  if (typeof window === "undefined") return null;
  return kind === "local" ? window.localStorage : window.sessionStorage;
}

function isStorageAvailable(kind: StorageKind) {
  const cached = availabilityCache.get(kind);
  if (typeof cached === "boolean") return cached;

  try {
    const storage = getNativeStorage(kind);
    if (!storage) {
      availabilityCache.set(kind, false);
      return false;
    }
    const probeKey = `__sxa_storage_probe__${kind}`;
    storage.setItem(probeKey, "1");
    storage.removeItem(probeKey);
    availabilityCache.set(kind, true);
    return true;
  } catch {
    availabilityCache.set(kind, false);
    return false;
  }
}

function createSafeStorage(kind: StorageKind): SafeStorageApi {
  return {
    getItem(key) {
      if (isStorageAvailable(kind)) {
        try {
          return getNativeStorage(kind)?.getItem(key) ?? null;
        } catch {
          availabilityCache.set(kind, false);
        }
      }
      return memoryFallback[kind].get(key) ?? null;
    },
    setItem(key, value) {
      if (isStorageAvailable(kind)) {
        try {
          getNativeStorage(kind)?.setItem(key, value);
          return;
        } catch {
          availabilityCache.set(kind, false);
        }
      }
      memoryFallback[kind].set(key, value);
    },
    removeItem(key) {
      if (isStorageAvailable(kind)) {
        try {
          getNativeStorage(kind)?.removeItem(key);
          return;
        } catch {
          availabilityCache.set(kind, false);
        }
      }
      memoryFallback[kind].delete(key);
    },
  };
}

export const safeLocalStorage = createSafeStorage("local");
export const safeSessionStorage = createSafeStorage("session");

export const safeStateStorage = {
  local: {
    getItem: (name: string) => safeLocalStorage.getItem(name),
    setItem: (name: string, value: string) => safeLocalStorage.setItem(name, value),
    removeItem: (name: string) => safeLocalStorage.removeItem(name),
  },
  session: {
    getItem: (name: string) => safeSessionStorage.getItem(name),
    setItem: (name: string, value: string) => safeSessionStorage.setItem(name, value),
    removeItem: (name: string) => safeSessionStorage.removeItem(name),
  },
};
